import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authApi, userApi, initializeCsrf, getErrorMessage } from "./api";
import { clearAllChatsFromIndexedDB } from "./utils/chatHelper";
import { getRelativePath } from "./utils/helpers";

const APIContext = createContext();

const APIProvider = ({ children }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const [activeTripData, setActiveTripData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initialContextFetch = async () => {
      // Initialize CSRF token first
      await initializeCsrf();

      // Try to get current user from session (cookie-based auth)
      let fetchedUserData = null;
      try {
        fetchedUserData = await userApi.getCurrentUser();
      } catch (error) {
        // User is not authenticated - this is expected for unauthenticated users
        console.log("User not authenticated:", error);
        setIsAuthenticated(false);
        setUserData({});
        return;
      }

      if (!fetchedUserData) {
        setIsAuthenticated(false);
        setUserData({});
        return;
      }

      setIsAuthenticated(true);
      setUserData(fetchedUserData);

      // Get trip data and navigate accordingly
      let tripData = null;
      try {
        tripData = await userApi.getActiveTrips();
      } catch (error) {
        console.error("Failed to fetch trip data:", error);
      }

      setActiveTripData(tripData);

      if (!tripData) {
        // If no active trip, clear chats
        clearAllChatsFromIndexedDB();
        return;
      }

      const path = getRelativePath();
      if (path.startsWith("/profile/") || path.startsWith("/chat")) {
        return;
      }

      if (fetchedUserData.user_type === "CLIENT") {
        if (tripData.status === "PENDING_OFFER") {
          navigate(`/searching-driver/${tripData.trip_id}`);
        } else {
          navigate(`/driver-found/${tripData.trip_id}`);
        }
      } else if (fetchedUserData.user_type === "DRIVER") {
        navigate(`/currentOrders/${tripData.trip_id}`);
      }
    };

    initialContextFetch()
      .then(() => {
        setLoading(false);
      });
  }, [navigate]);

  // Handle global session expiry events (triggered by Axios interceptor)
  useEffect(() => {
    const handleSessionExpired = () => {
      setUserData({});
      setIsAuthenticated(false);
    };
      
    // custom event fired from client.js
    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, []);

  const logout = async () => {
    try {
      await authApi.logoutSession();
      setUserData({});
      setIsAuthenticated(false);
      // Navigate to login page after logout
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      const message = getErrorMessage(error);
      toast.error(message || "Failed to log out", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const switchProfile = async (profile = null) => {
    try {
      const desiredType = profile || (userData.user_type === "CLIENT" ? "DRIVER" : "CLIENT");
      const data = await authApi.switchProfile(desiredType);
      setUserData(data);
    } catch (error) {
      console.error("Switch profile error:", error);
      const message = getErrorMessage(error);
      toast.error(message || "Failed to switch profile", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  /**
   * Refresh user data from the server
   * Call this after authentication state changes
   */
  const refreshUserData = async () => {
    try {
      const fetchedUserData = await userApi.getCurrentUser();
      if (fetchedUserData) {
        setUserData(fetchedUserData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  };

  return (
    <APIContext.Provider
      value={{
        logout,
        switchProfile,
        refreshUserData,
        activeTripData,
        userData,
        setUserData,
        loading,
        isAuthenticated,
        setIsAuthenticated,
      }}
    >
      {children}
    </APIContext.Provider>
  );
};

export { APIProvider, APIContext };
